import styled, { keyframes } from "styled-components";

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const Container = styled.div`
  padding: 32px;
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  min-height: 100vh;
  background: var(--background);
  animation: ${fadeIn} 0.4s ease-out;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

export const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 80vh;
  gap: 1rem;
  color: var(--text-secondary);

  svg {
    animation: ${rotate} 1s linear infinite;
    color: var(--primary);
  }
`;

export const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 24px;
  }

  .title-area {
    h2 {
      font-size: 2.25rem;
      color: var(--text-main);
      font-weight: 900;
      margin: 0;
      letter-spacing: -1px;
    }
    p {
      color: var(--text-secondary);
      margin-top: 6px;
      font-size: 1.1rem;
      font-weight: 500;
    }
  }
`;

export const SegmentedControl = styled.div`
  display: flex;
  background: #e2e8f0;
  padding: 4px;
  border-radius: 14px;
  gap: 2px;
  flex-shrink: 0;

  @media (max-width: 480px) {
    width: 100%;
    overflow-x: auto;
  }

  button {
    all: unset;
    cursor: pointer;
    padding: 10px 24px;
    border-radius: 10px;
    font-size: 0.875rem;
    font-weight: 700;
    color: var(--text-secondary);
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    white-space: nowrap;
    text-align: center;

    &.active {
      background: var(--surface);
      color: var(--primary);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }

    &:hover:not(.active) {
      color: var(--text-main);
      background: rgba(255, 255, 255, 0.5);
    }
  }
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 24px;
  margin-bottom: 40px;

  .stat-card {
    background: var(--surface);
    padding: 32px;
    border-radius: 20px;
    border: 1px solid var(--border);
    box-shadow: var(--shadow-sm);
    display: flex;
    flex-direction: column;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-6px);
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
      border-color: var(--primary);
    }

    .icon-box {
      width: 52px;
      height: 52px;
      border-radius: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 24px;

      &.green {
        background: #ecfdf5;
        color: #10b981;
      }
      &.blue {
        background: #eff6ff;
        color: #3b82f6;
      }
      &.purple {
        background: #faf5ff;
        color: #a855f7;
      }
      &.orange {
        background: #fff7ed;
        color: #f97316;
      }
    }

    span {
      font-size: 0.8rem;
      color: var(--text-secondary);
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.8px;
    }

    h3 {
      font-size: 2.25rem;
      color: var(--text-main);
      font-weight: 900;
      margin: 8px 0 0 0;
      letter-spacing: -0.5px;
    }
  }
`;

export const TableCard = styled.div`
  background: var(--surface);
  border-radius: 20px;
  border: 1px solid var(--border);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  width: 100%;

  .table-header {
    padding: 24px 32px;
    border-bottom: 1px solid var(--border);
    display: flex;
    justify-content: space-between;
    align-items: center;

    h4 {
      font-size: 1.35rem;
      color: var(--text-main);
      font-weight: 800;
      margin: 0;
    }
  }
`;

export const MainTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  thead {
    background: #f8fafc;
    th {
      text-align: left;
      padding: 18px 32px;
      font-size: 0.75rem;
      font-weight: 800;
      color: var(--text-secondary);
      text-transform: uppercase;
      letter-spacing: 1.2px;
    }
  }

  tbody tr {
    border-bottom: 1px solid var(--border);
    transition: all 0.2s;

    &:hover {
      background: #f1f5f9;
    }

    &:last-child {
      border: none;
    }

    td {
      padding: 24px 32px;
      color: var(--text-main);
      font-size: 1rem;

      .customer-info {
        strong {
          font-weight: 800;
          display: block;
          font-size: 1.05rem;
        }
        small {
          color: var(--text-secondary);
          font-size: 0.85rem;
          font-weight: 500;
        }
      }

      .payment-tag {
        background: #fff;
        padding: 6px 12px;
        border-radius: 10px;
        font-size: 0.7rem;
        font-weight: 900;
        color: var(--text-secondary);
        border: 1px solid #e2e8f0;
        text-transform: uppercase;
      }
    }
  }

  @media (max-width: 960px) {
    thead {
      display: none;
    }
    tbody tr {
      display: block;
      padding: 20px;
      border-bottom: 8px solid #f1f5f9;

      td {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 0;
        border-bottom: 1px solid #f1f5f9;

        &:last-child {
          border: none;
        }

        &:before {
          content: attr(data-label);
          font-weight: 800;
          color: var(--text-secondary);
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
      }
    }
  }
`;

export const PriceText = styled.strong`
  color: var(--text-main);
  font-weight: 900;
  font-size: 1.2rem;
  letter-spacing: -0.5px;
`;

export const StatusBadge = styled.span`
  background: #dcfce7;
  color: #166534;
  padding: 6px 16px;
  border-radius: 99px;
  font-size: 0.7rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid rgba(22, 101, 52, 0.1);
`;
